import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, PlusIcon, MinusIcon, UserRoundIcon } from 'lucide-react';
interface Testimonial {
  name: string;
  role: string;
  quote: string;
  initial: string;
}
const TESTIMONIALS: Testimonial[] = [{
  name: 'S. Cesar',
  role: 'Office & home cleaning',
  quote: "Good and reliable service. I'm happy to have them to clean my messy office. I recommend them for their best cleaning services!",
  initial: 'S'
}, {
  name: 'Danna J.',
  role: 'Spotless apartment',
  quote: 'Ally cleaned my apartment and left it spotless. Amazing service and very professional.',
  initial: 'D'
}, {
  name: 'Elena J.',
  role: 'Professional & punctual',
  quote: 'I had an excellent experience with Ally Cleaning Services! Their team was professional, punctual, and did an outstanding job.',
  initial: 'E'
}];
interface FAQ {
  q: string;
  a: string;
}
const FAQS: FAQ[] = [{
  q: 'Do I need to provide cleaning supplies?',
  a: 'No. We bring everything needed for a premium clean, including professional-grade supplies and equipment.'
}, {
  q: 'Are you pet friendly?',
  a: 'Yes — we love pets and use thoughtful practices to keep your furry family members comfortable and safe.'
}, {
  q: 'What is your cancellation policy?',
  a: 'We kindly require 24 hours’ notice. Appointments cancelled with less notice are subject to a 50% fee.'
}, {
  q: 'Do you offer Deep Cleaning?',
  a: 'Yes. We recommend a Deep Cleaning for first visits so we can establish the immaculate boutique standard your home deserves.'
}, {
  q: 'How do I pay?',
  a: 'For your convenience, we accept Zelle, Cash, and all major credit and debit cards.'
}, {
  q: 'Do you do laundry?',
  a: 'Yes. Laundry service is available as a tailored add-on to your cleaning appointment.'
}, {
  q: 'Are you insured?',
  a: 'Yes. Our team is professionally insured, so you can enjoy complete peace of mind.'
}, {
  q: 'What areas do you serve?',
  a: 'We proudly serve Manhattan, Queens, and Long Island.'
}, {
  q: 'Can I trust you with my keys?',
  a: 'Absolutely. We use secure key-management practices and the utmost discretion in every home we enter.'
}, {
  q: 'Do you offer organic products?',
  a: 'Yes. Organic and eco-conscious products are available upon request for a clean that feels good in every sense.'
}];
function Stars() {
  return <div className="flex gap-0.5" aria-label="5 out of 5 stars" data-id="element-250" data-mp="208om0w7vqq4e">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-magenta text-magenta" data-id="element-251" data-mp="252strhoyz26j" data-mpi={i} />)}
    </div>;
}
export function SocialProof() {
  const [open, setOpen] = useState<number | null>(0);
  return <section className="w-full bg-white" data-id="element-252" data-mp="2ewswjcws3vc3">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-32" data-id="element-253" data-mp="2jxgyuuqgjylm">
        {/* Testimonials */}
        <div className="mx-auto max-w-2xl text-center" data-id="element-254" data-mp="8b6fjyumkr1x">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-magenta" data-id="element-255" data-mp="88k7zz7qibjp">
            Loved by New Yorkers
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate" data-id="element-256" data-mp="3uhbokdpge3an">
            What our clients say
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:mt-14 md:grid-cols-3" data-id="element-257" data-mp="3pl4srdqru86f">
          {TESTIMONIALS.map((t, i) => <motion.figure key={t.name} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.08
        }} className="relative flex flex-col rounded-3xl bg-slate-50 p-8 ring-1 ring-slate-100" data-id="element-258" data-mp="m2be0xm9bdao" data-mpi={i}>
              <div className="flex items-start justify-between gap-4" data-id="element-259" data-mp="pl8yjtynl9c4">
                <Stars data-id="element-260" data-mp="ujk781d7pdsr" />
                <span aria-label="Google review" className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white font-display text-sm font-extrabold text-[#4285f4] shadow-sm ring-1 ring-slate-100" data-id="element-261" data-mp="ufmm5a72qc68">
                  G
                </span>
              </div>
              <blockquote className="mt-5 flex-1 text-slate-soft leading-relaxed" data-id="element-262" data-mp="1d90a2vcddicc">
                “{t.quote}”
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3" data-id="element-263" data-mp="yj1ff2k0wp1m">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-magenta ring-1 ring-slate-200" data-id="element-264" data-mp="13hvvvxuiq1ik">
                  <UserRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-265" data-mp="1r6xa61q186vr" />
                  <span className="sr-only" data-id="element-266" data-mp="1vzqeiohl9d7b">{t.initial}</span>
                </span>
                <div data-id="element-267" data-mp="3golqk2zxrrqu">
                  <p className="font-display text-sm font-bold text-slate" data-id="element-268" data-mp="3gnst5smvaeld">
                    {t.name}
                  </p>
                  <p className="text-xs text-slate-muted" data-id="element-269" data-mp="3bo6ctabqsi8c">{t.role}</p>
                </div>
              </figcaption>
            </motion.figure>)}
        </div>

        {/* FAQ */}
        <div id="faq" className="mx-auto mt-16 max-w-3xl sm:mt-24" data-id="element-270" data-mp="3lgvlxyjtlk0i">
          <div className="text-center" data-id="element-271" data-mp="2xrkn4mm15zp0">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-brand" data-id="element-272" data-mp="2xschju3w5rdp">
              FAQ
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate" data-id="element-273" data-mp="36rphnw64l3lr">
              Questions, answered
            </h2>
          </div>

          <div className="mt-10 divide-y divide-slate-100 rounded-3xl ring-1 ring-slate-100" data-id="element-274" data-mp="m0yq7a8rcn98">
            {FAQS.map((faq, i) => {
            const isOpen = open === i;
            return <div key={faq.q} data-id="element-275" data-mp="3atu6z2znz810" data-mpi={i}>
                  <button type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left" data-id="element-276" data-mp="3aun4ddcqgl6h">
                    <span className="font-display text-base font-semibold text-slate" data-id="element-277" data-mp="3ou4zeagmguoa">
                      {faq.q}
                    </span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-magenta-soft text-magenta" data-id="element-278" data-mp="2s4x8so15bvdi">
                      {isOpen ? <MinusIcon className="h-4 w-4" data-id="element-279" data-mp="313sfe183r7hc" /> : <PlusIcon className="h-4 w-4" data-id="element-280" data-mp="35vajuz9c3q72" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false} data-id="element-281" data-mp="29c37uy0cy3sa">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.28
                }} className="overflow-hidden" data-id="element-282" data-mp="2ddeowf561gov">
                        <p className="px-6 pb-5 text-slate-soft leading-relaxed" data-id="element-283" data-mp="1i41nlbsu36on">
                          {faq.a}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}